import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Stack } from '@mui/material';
import DateTimePicker from '../DateTimePicker/DateTimePicker';
import InlineEditDisplay from '../InlineEditDisplay/InlineEditDisplay';
import { useInlineEditDateTimePicker } from './hooks/useInlineEditDateTimePicker';
import { InlineEditDateTimePickerProps } from './inlineEditDateTimePicker.types';

const InlineEditDateTimePicker = ({
  value,
  displayLabel,
  minDateTime,
  maxDateTime,
  popupSize = 'compact',
  isDisabled,
  hasError,
  placeholder,
  testId,
  validate,
  onConfirm,
}: InlineEditDateTimePickerProps) => {
  const {
    containerRef,
    error,
    isEditing,
    isPopupOpen,
    draftValue,
    handleStartEditing,
    handleChangeDraft,
    handleConfirm,
    handleBlur,
    handleKeyDown,
    openPopup,
    closePopup,
  } = useInlineEditDateTimePicker({ value, isDisabled, validate, onConfirm });

  if (!isEditing) {
    return (
      <InlineEditDisplay
        isEmpty={!value}
        isDisabled={Boolean(isDisabled)}
        hasError={hasError}
        placeholder={placeholder}
        testId={testId}
        onStartEditing={handleStartEditing}
      >
        {displayLabel}
      </InlineEditDisplay>
    );
  }

  return (
    <Stack sx={{ gap: 0.25 }}>
      <Box
        ref={containerRef}
        sx={(theme) => ({
          '& .MuiOutlinedInput-root': {
            ...theme.typography.body2,
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.action.selected,
            borderRadius: `${theme.shape.borderRadiusMd}px`,
          },
          '& .MuiInputBase-inputSizeSmall': {
            padding: theme.spacing(0.5, 0.75),
            height: 'auto',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '& .MuiInputAdornment-root': {
            maxHeight: 'none',
          },
          '& .MuiInputAdornment-root .MuiButtonBase-root': {
            width: 'auto',
            height: 'auto',
            padding: theme.spacing(0.25),
          },
        })}
      >
        <DateTimePicker
          autoFocus
          size="small"
          popupSize={popupSize}
          open={isPopupOpen}
          value={draftValue}
          minDateTime={minDateTime}
          maxDateTime={maxDateTime}
          disabled={isDisabled}
          error={hasError || Boolean(error)}
          onChange={handleChangeDraft}
          onAccept={handleConfirm}
          onOpen={openPopup}
          onClose={closePopup}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
        />
      </Box>
      {error && (
        <Typography variant="micro1" color="error.main">
          {error}
        </Typography>
      )}
    </Stack>
  );
};

export default InlineEditDateTimePicker;
